import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { db } from "../../firebase";
import {
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  doc,
  setDoc,
  getDoc,
} from "firebase/firestore";
import { useAuth } from "../../context/AuthContext";
import logoImg from "../../assets/logo.png";

export default function Chat() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [chatId, setChatId] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [botActive, setBotActive] = useState(true);
  const messagesEndRef = useRef(null);

  // 🔹 Setup chat doc (user ↔ admin)
  useEffect(() => {
    if (!user) return;

    const setupChat = async () => {
      const participants = [user.email, "admin@convalesce.com"].sort();
      const chatKey = participants.join("_").replace(/[.@]/g, "_");
      const chatRef = doc(db, "chats", chatKey);

      const chatSnap = await getDoc(chatRef);
      if (!chatSnap.exists()) {
        await setDoc(chatRef, { participants, createdAt: serverTimestamp() });
      }
      setChatId(chatKey);
    };

    setupChat();
  }, [user]);

  // 🔹 Listen for messages
  useEffect(() => {
    if (!chatId) return;

    const q = query(
      collection(db, "chats", chatId, "messages"),
      orderBy("timestamp", "asc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setMessages(snapshot.docs.map((doc) => doc.data()));
    });

    return () => unsubscribe();
  }, [chatId]);

  // 🔹 Auto-scroll
  useEffect(() => {
    const timeout = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
    return () => clearTimeout(timeout);
  }, [messages]);

  // 🔹 Simple Bot Logic
  const getBotReply = (text) => {
    const lower = text.toLowerCase();
    if (lower.includes("hello") || lower.includes("hi"))
      return "Hello 👋! I’m Convalesce Assistant. How can I help you today?";
    if (lower.includes("help"))
      return "Sure! Could you tell me what kind of help you need?";
    if (lower.includes("pay") || lower.includes("timesheet"))
      return "For timesheets or payslips, please visit the Timesheets section 📄";
    return "Our admin will be with you shortly 😊";
  };

  // 🔹 Send Message
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const text = newMessage.trim();

    await addDoc(collection(db, "chats", chatId, "messages"), {
      text,
      senderEmail: user.email,
      senderName: user.name || user.email,
      timestamp: serverTimestamp(),
    });

    setNewMessage("");

    // Bot auto reply
    if (botActive) {
      setIsTyping(true);
      setTimeout(async () => {
        const reply = getBotReply(text);
        await addDoc(collection(db, "chats", chatId, "messages"), {
          text: reply,
          senderEmail: "bot@convalesce.com",
          senderName: "Convalesce Assistant",
          timestamp: serverTimestamp(),
        });
        setIsTyping(false);
      }, 1500);
      if (messages.length > 4) setBotActive(false);
    }
  };

  return (
    <div
      style={{
        paddingTop: "10px",
        minHeight: "100vh",
        backgroundColor: "rgba(55,65,81,0.8)",
        color: "#f3f4f6",
        padding: "1.5rem",
		paddingBottom: "50px"
      }}
    >
      {/* Header */}
      <h1
        style={{
          textAlign: "center",
          color: "gray",
          backgroundColor: "white",
          boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
          width: "fit-content",
          margin: "40px auto 0 auto",
          fontSize: "16px",
          fontWeight: "500",
          padding: "14px 24px",
          borderRadius: "20px",
        }}
      >
        Chat with Admin
      </h1>

      {/* Chat Card */}
      <div
        style={{
          maxWidth: "900px",
          margin: "40px auto",
          backgroundColor: "white",
          borderRadius: "16px",
          padding: "24px",
          color: "#1f2937",
          boxShadow: "0 4px 8px rgba(0,0,0,0.05)",
          display: "flex",
          flexDirection: "column",
          height: "70vh",
        }}
      >
        {/* Top Section: Company + Status */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            borderBottom: "1px solid #e5e7eb",
            paddingBottom: "10px",
            marginBottom: "10px",
          }}
        >
          <div
			  style={{
				width: 62,
				height: 62,
				borderRadius: "50%",
				border: "2px solid #9ca3af", // 👈 wider border
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				backgroundColor: "#fff",
				boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
				marginRight: 12,
			  }}
			>
			  <img
				src={logoImg}
				alt="Convalesce Logo"
				style={{
				  width: 40,
				  height: 40,
				  borderRadius: "50%",
				}}
			  />
			</div>
          <div>
            <h2 style={{ fontSize: "18px", fontWeight: "700" }}>
              CCR — Chat
            </h2>
            <p style={{ fontSize: "13px", color: "#6b7280" }}>
              Your comfort, your choice, your care
            </p>
          </div>
        </div>

        {/* Messages Area */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            backgroundColor: "#f9fafb",
            borderRadius: "12px",
            padding: "16px",
            marginBottom: "10px",
            border: "1px solid #e5e7eb",
          }}
        >
          {messages.length === 0 && (
            <p style={{ textAlign: "center", color: "#9ca3af", marginTop: "12px" }}>
              No messages yet. Say hello 👋
            </p>
          )}

          {messages.map((msg, index) => (
            <motion.div
              key={index}
              style={{
                display: "flex",
                justifyContent:
                  msg.senderEmail === user.email ? "flex-end" : "flex-start",
                marginBottom: "8px",
              }}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
            >
              <div
                style={{
                  backgroundColor:
                    msg.senderEmail === user.email
                      ? "#d1d5db"
                      : msg.senderEmail === "bot@convalesce.com"
                      ? "#e0f2fe"
                      : "#f3f4f6",
                  color: "#111827",
                  borderRadius: "12px",
                  padding: "8px 12px",
                  maxWidth: "70%",
                  fontSize: "14px",
                  boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
                }}
              >
                <p>{msg.text}</p>
                <span
                  style={{
                    display: "block",
                    fontSize: "11px",
                    color: "#6b7280",
                    marginTop: "4px",
                    textAlign:
                      msg.senderEmail === user.email ? "right" : "left",
                  }}
                >
                  {msg.senderEmail === user.email
                    ? "You"
                    : msg.senderEmail === "bot@convalesce.com"
                    ? "Assistant"
                    : "Admin"}
                </span>
              </div>
            </motion.div>
          ))}

          {isTyping && (
            <p style={{ fontStyle: "italic", color: "#9ca3af", fontSize: "13px" }}>
              Admin is typing...
            </p>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <form
          onSubmit={sendMessage}
          style={{
            display: "flex",
            gap: "8px",
            alignItems: "center",
            marginTop: "auto",
          }}
        >
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message..."
            style={{
              flex: 1,
              padding: "12px",
              borderRadius: "10px",
              border: "1px solid #d1d5db",
              backgroundColor: "#f9fafb",
              fontSize: "14px",
              outline: "none",
            }}
          />
          <button
            type="submit"
            style={{
              backgroundColor: "#1e3a8a",
              color: "white",
              padding: "10px 16px",
              borderRadius: "10px",
              fontWeight: "500",
              cursor: "pointer",
            }}
          >
            Send
          </button>
        </form>
      </div>

      {/* Footer */}
      <div
        style={{
          textAlign: "center",
          color: "#9ca3af",
          marginTop: "30px",
          fontSize: "13px",
        }}
      >
        
      </div>
    </div>
  );
}
